/**
 * Dashboard API hook — Phase 9.
 *
 * GET /v1/dashboard with 60-second auto-refresh (refetchInterval).
 * Returns DashboardResponse with all KPI counters, upcoming sessions,
 * and recent payments.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@core/api/client';

// ── Types ────────────────────────────────────────────────────────────────────

export interface UpcomingSession {
  session_id: string;
  class_name: string;
  subject_name: string;
  starts_at: string; // ISO 8601 UTC
  teacher_name: string | null;
  student_count: number;
}

export interface RecentPayment {
  payment_id: string;
  student_name: string | null;
  amount: number; // paise
  method: string;
  paid_at: string; // ISO 8601 UTC
  invoice_number: string | null;
}

export interface DashboardData {
  active_students: number;
  active_classes: number;
  sessions_today: number;
  sessions_this_week: number;
  outstanding_invoices_total: number; // paise
  payments_this_month_total: number; // paise
  expiring_entitlements_30d: number;
  upcoming_sessions: UpcomingSession[];
  recent_payments: RecentPayment[];
  generated_at: string; // ISO 8601 UTC
  errors: string[];
}

// ── Query key ────────────────────────────────────────────────────────────────

export const DASHBOARD_QUERY_KEY = ['dashboard'] as const;

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useDashboard() {
  return useQuery<DashboardData>({
    queryKey: DASHBOARD_QUERY_KEY,
    queryFn: () => api.get<DashboardData>('/v1/dashboard'),
    refetchInterval: 60_000, // auto-refresh every 60 seconds
    staleTime: 30_000,
  });
}

// ── 11E.8 — role-scoped dashboard variants ─────────────────────────────────
//
// Types mirror backend/app/features/dashboard/schemas.py and
// backend/app/features/reports/operations/schemas.py exactly.

export interface LeaveBalance {
  staff_id: string;
  year: number;
  allocated: number;
  availed: number;
  balance: number;
}

export interface AttendanceConcernEntry {
  student_id: string;
  student_name: string;
  consecutive_missed: number;
  pct_present: number | null;
  reason: 'consecutive' | 'percentage' | 'both';
}

export interface MyDayDashboardData {
  leave_balance: LeaveBalance;
  attendance_concerns: AttendanceConcernEntry[];
  generated_at: string;
  errors: string[];
}

export const MY_DAY_DASHBOARD_QUERY_KEY = ['dashboard', 'my-day'] as const;

export function useMyDayDashboard() {
  return useQuery<MyDayDashboardData>({
    queryKey: MY_DAY_DASHBOARD_QUERY_KEY,
    queryFn: () => api.get<MyDayDashboardData>('/v1/dashboard/my-day'),
    staleTime: 30_000,
  });
}

export interface AttendanceConcernsData {
  concerns: AttendanceConcernEntry[];
  generated_at: string;
}

export const ATTENDANCE_CONCERNS_QUERY_KEY = ['dashboard', 'attendance-concerns'] as const;

export function useAttendanceConcerns() {
  return useQuery<AttendanceConcernsData>({
    queryKey: ATTENDANCE_CONCERNS_QUERY_KEY,
    queryFn: () => api.get<AttendanceConcernsData>('/v1/dashboard/attendance-concerns'),
    staleTime: 30_000,
  });
}

export type PaymentDuePeriod = 'this_month' | 'next_month' | 'custom';

export interface PaymentDueRow {
  student_id: string;
  student_name: string;
  guardian_name: string | null;
  guardian_contact: string | null;
  invoice_id: string;
  invoice_number: string | null;
  amount_due: number; // paise
  due_date: string;
  overdue_days: number;
}

export interface PaymentDueReport {
  period: PaymentDuePeriod;
  from_date: string;
  to_date: string;
  rows: PaymentDueRow[];
  total_count: number;
  total_amount_due: number; // paise
}

export interface TodaysCollections {
  count: number;
  total: number; // paise
}

export interface OfficeDashboardData {
  todays_collections: TodaysCollections;
  pending_payments: PaymentDueReport;
  generated_at: string;
  errors: string[];
}

export const OFFICE_DASHBOARD_QUERY_KEY = ['dashboard', 'office'] as const;

export function useOfficeDashboard() {
  return useQuery<OfficeDashboardData>({
    queryKey: OFFICE_DASHBOARD_QUERY_KEY,
    queryFn: () => api.get<OfficeDashboardData>('/v1/dashboard/office'),
    staleTime: 30_000,
  });
}

export type BillingCycle = 'monthly' | 'quarterly' | 'half_yearly' | 'yearly' | 'unknown';

export interface RenewalCycleBucket {
  cycle: BillingCycle;
  projections_count: number;
  projections_amount: number; // paise
  received_count: number;
  received_amount: number; // paise
}

export interface SubjectBreakdownEntry {
  subject_id: string | null;
  subject_name: string;
  count: number;
}

export interface NewEnrolmentsCycleBucket {
  cycle: BillingCycle;
  count: number;
  amount: number; // paise
}

export interface DropsCycleBucket {
  cycle: BillingCycle;
  count: number;
}

export interface DailyStatusDigestData {
  tenant_id: string;
  tenant_name: string;
  date: string;
  month_label: string;
  total_projections: number; // paise
  total_received: number; // paise
  todays_collection: number; // paise
  renewals_by_cycle: RenewalCycleBucket[];
  total_admissions_this_month: number;
  todays_admissions_by_subject: SubjectBreakdownEntry[];
  demos_today: number;
  demos_today_by_subject: SubjectBreakdownEntry[];
  new_enrolments_by_cycle: NewEnrolmentsCycleBucket[];
  drops_by_cycle: DropsCycleBucket[];
  generated_at: string;
  errors: string[];
}

export function dailyStatusQueryKey(date: string) {
  return ['dashboard', 'daily-status', date] as const;
}

export function useDailyStatusDigest(date: string) {
  return useQuery<DailyStatusDigestData>({
    queryKey: dailyStatusQueryKey(date),
    queryFn: () => api.get<DailyStatusDigestData>('/v1/dashboard/daily-status', { query: { date } }),
    staleTime: 30_000,
  });
}

// ── Formatting helpers ───────────────────────────────────────────────────────

/** Convert paise (integer) to a human-readable INR string. */
export function formatAmount(paise: number): string {
  const rupees = paise / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(rupees);
}

/** Same as formatAmount, but renders exactly "NIL" for a zero/absent value —
 * the Daily Status Digest's convention (matches the owner's WhatsApp report). */
export function formatAmountOrNil(paise: number | null | undefined): string {
  if (!paise) return 'NIL';
  return formatAmount(paise);
}

/** Same NIL convention for plain counts (admissions, demos, drops, …). */
export function formatCountOrNil(count: number | null | undefined): string {
  if (!count) return 'NIL';
  return new Intl.NumberFormat('en-IN').format(count);
}

